import { useState, useEffect } from 'react'
import { Ruler, Plus, Trash2, Edit, Search, X, User, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button, LoadingSpinner } from '../components'
import { useCustomerMeasurements } from '../hooks/useCustomerMeasurements'
import customerMeasurementService from '../services/customerMeasurementService'
import measurementFieldConfigService from '../services/measurementFieldConfigService'
import customerService from '../services/customerService'
import toast from 'react-hot-toast'

// ── Measurement Form Modal ──────────────────────────────────────────────────
function MeasurementFormModal({ isOpen, editingMeasurement, onClose, onSubmit, isLoading }) {
  const [customers, setCustomers] = useState([])
  const [garmentCategories, setGarmentCategories] = useState([])
  const [fieldConfigs, setFieldConfigs] = useState([])
  const [values, setValues] = useState({})
  const [formData, setFormData] = useState({
    customerId: '',
    garmentCategory: '',
    notes: '',
  })
  const [isFetchingFields, setIsFetchingFields] = useState(false)
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)

  // Load customers + garment categories when modal opens
  useEffect(() => {
    if (!isOpen) return
    const fetchInitialData = async () => {
      setIsLoadingCustomers(true)
      try {
        const [customersRes, categoriesRes] = await Promise.all([
          customerService.getAll({ limit: 10 }),
          measurementFieldConfigService.getGarmentCategories(),
        ])
        setCustomers(customersRes.data.customers || [])
        setGarmentCategories(categoriesRes.data.categories || [])
      } catch {
        toast.error('Failed to load form data')
      } finally {
        setIsLoadingCustomers(false)
      }
    }
    fetchInitialData()
  }, [isOpen])

  // Pre-populate when editing
  useEffect(() => {
    if (editingMeasurement) {
      setFormData({
        customerId: editingMeasurement.customerId || '',
        garmentCategory: editingMeasurement.garmentCategory || '',
        notes: editingMeasurement.notes || '',
      })
      setValues(editingMeasurement.values || {})
    } else {
      setFormData({ customerId: '', garmentCategory: '', notes: '' })
      setValues({})
      setFieldConfigs([])
    }
  }, [editingMeasurement, isOpen])

  // Fetch dynamic field configs when garmentCategory changes
  useEffect(() => {
    const cat = formData.garmentCategory
    if (!cat) {
      setFieldConfigs([])
      return
    }
    const fetchFields = async () => {
      setIsFetchingFields(true)
      try {
        const res = await measurementFieldConfigService.getAll({ garmentCategory: cat })
        const configs = res.data.configs || []
        setFieldConfigs(configs)
        // Initialize missing value keys with empty string
        setValues((prev) => {
          const next = { ...prev }
          configs.forEach((c) => {
            if (next[c.fieldKey] === undefined) next[c.fieldKey] = ''
          })
          return next
        })
      } catch {
        toast.error('Failed to load measurement fields')
      } finally {
        setIsFetchingFields(false)
      }
    }
    fetchFields()
  }, [formData.garmentCategory])

  const handleChange = (key, val) => {
    setFormData((prev) => ({ ...prev, [key]: val }))
  }

  const handleValueChange = (fieldKey, val) => {
    setValues((prev) => ({ ...prev, [fieldKey]: val }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.customerId) { toast.error('Please select a customer'); return }
    if (!formData.garmentCategory) { toast.error('Please select a garment category'); return }
    // Clean values: convert empty strings to null, parse numbers
    const cleanedValues = {}
    Object.entries(values).forEach(([k, v]) => {
      cleanedValues[k] = v === '' ? null : parseFloat(v) || v
    })
    onSubmit({ ...formData, values: cleanedValues })
  }

  const handleClose = () => {
    setFormData({ customerId: '', garmentCategory: '', notes: '' })
    setValues({})
    setFieldConfigs([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-6 pt-5 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-primary-100">
                  <Ruler className="h-5 w-5 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-secondary-900">
                    {editingMeasurement ? 'Edit Measurement' : 'Add Measurement'}
                  </h3>
                  <p className="text-sm text-secondary-500">
                    {editingMeasurement ? 'Update measurement values' : 'Enter customer measurement details'}
                  </p>
                </div>
              </div>
              <button onClick={handleClose} className="text-secondary-400 hover:text-secondary-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body max-h-[30rem] overflow-y-auto space-y-4">
                {/* Customer + Garment Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Customer *
                    </label>
                    <select
                      className="input"
                      value={formData.customerId}
                      onChange={(e) => handleChange('customerId', e.target.value)}
                      disabled={isLoadingCustomers || !!editingMeasurement}
                    >
                      <option value="">
                        {isLoadingCustomers ? 'Loading...' : 'Select Customer'}
                      </option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.firstName} {c.lastName} — {c.phone}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Garment Category *
                    </label>
                    <select
                      className="input"
                      value={formData.garmentCategory}
                      onChange={(e) => handleChange('garmentCategory', e.target.value)}
                      disabled={!!editingMeasurement}
                    >
                      <option value="">Select Category</option>
                      {garmentCategories.map((cat) => (
                        <option key={cat} value={cat} className="capitalize">
                          {cat}
                        </option>
                      ))}
                    </select>
                    {garmentCategories.length === 0 && !isLoadingCustomers && (
                      <p className="text-xs text-danger-600 mt-1">
                        No categories configured. Go to Measurement Setup first.
                      </p>
                    )}
                  </div>
                </div>

                {/* Dynamic measurement fields */}
                {isFetchingFields ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : fieldConfigs.length > 0 ? (
                  <div>
                    <h4 className="text-base font-medium text-secondary-900 mb-3 border-b border-secondary-200 pb-2 capitalize">
                      {formData.garmentCategory} Measurements
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {fieldConfigs.map((config) => (
                        <div key={config.fieldKey}>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            {config.label}
                            <span className="text-secondary-400 ml-1 font-normal">
                              ({config.unit})
                            </span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="input text-base"
                            placeholder="0.00"
                            value={values[config.fieldKey] ?? ''}
                            onChange={(e) => handleValueChange(config.fieldKey, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : formData.garmentCategory ? (
                  <p className="text-sm text-secondary-400 italic py-2">
                    No measurement fields configured for "{formData.garmentCategory}".
                    Edit the category in Measurement Setup.
                  </p>
                ) : null}

                {/* Notes */}
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Any additional notes..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                  />
                </div>
              </div>

              <div className="modal-footer flex justify-end space-x-3 mt-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                  {isLoading ? 'Saving...' : editingMeasurement ? 'Update' : 'Save Measurement'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────
function CustomerMeasurements() {
  const {
    measurements,
    isLoading,
    totalPages,
    totalItems,
    currentPage,
    isCreating,
    isUpdating,
    isDeleting,
    showModal,
    editingMeasurement,
    searchTerm,
    categoryFilter,
    setSearchTerm,
    setCategoryFilter,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
  } = useCustomerMeasurements()

  const [garmentCategories, setGarmentCategories] = useState([])

  useEffect(() => {
    measurementFieldConfigService
      .getGarmentCategories()
      .then((res) => setGarmentCategories(res.data.categories || []))
      .catch(() => {})
  }, [])

  const isSubmitting = isCreating || isUpdating

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Customer Measurements</h1>
          <p className="text-secondary-500 mt-1">
            {totalItems} measurement{totalItems !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleEdit(null)}
          className="flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Measurement
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-secondary-200 p-4 flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            className="input pl-9"
            placeholder="Search by customer name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category filter */}
        <select
          className="input w-48"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {garmentCategories.map((cat) => (
            <option key={cat} value={cat} className="capitalize">
              {cat}
            </option>
          ))}
        </select>

        {(searchTerm || categoryFilter) && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center text-sm text-secondary-500 hover:text-secondary-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : measurements.length === 0 ? (
          <div className="text-center py-16">
            <Ruler className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-base font-medium text-secondary-900 mb-1">No measurements found</h3>
            <p className="text-secondary-500 text-sm">
              {searchTerm || categoryFilter
                ? 'Try clearing filters'
                : 'Add your first customer measurement'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-secondary-600">Customer</th>
                    <th className="text-left px-4 py-3 font-medium text-secondary-600">Category</th>
                    <th className="text-left px-4 py-3 font-medium text-secondary-600">Key Measurements</th>
                    <th className="text-left px-4 py-3 font-medium text-secondary-600">Notes</th>
                    <th className="text-left px-4 py-3 font-medium text-secondary-600">Date</th>
                    <th className="text-right px-4 py-3 font-medium text-secondary-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {measurements.map((m) => {
                    const valueEntries = Object.entries(m.values || {}).filter(
                      ([, v]) => v !== null && v !== ''
                    )
                    const previewEntries = valueEntries.slice(0, 3)

                    return (
                      <tr key={m.id} className="hover:bg-secondary-50 transition-colors">
                        {/* Customer */}
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center mr-3 flex-shrink-0">
                              <User className="h-4 w-4 text-primary-600" />
                            </div>
                            <div>
                              <p className="font-medium text-secondary-900">
                                {m.customer?.firstName} {m.customer?.lastName}
                              </p>
                              <p className="text-xs text-secondary-500">{m.customer?.phone}</p>
                            </div>
                          </div>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                            {m.garmentCategory}
                          </span>
                        </td>

                        {/* Key measurements */}
                        <td className="px-4 py-3">
                          {previewEntries.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {previewEntries.map(([k, v]) => (
                                <span
                                  key={k}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700"
                                >
                                  {k}: {v}
                                </span>
                              ))}
                              {valueEntries.length > 3 && (
                                <span className="text-xs text-secondary-400">
                                  +{valueEntries.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-secondary-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Notes */}
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-secondary-600 text-xs truncate">
                            {m.notes || '—'}
                          </p>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3 text-secondary-500 text-xs whitespace-nowrap">
                          {new Date(m.createdAt).toLocaleDateString()}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(m)}
                              className="p-1.5 rounded-md text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={isDeleting}
                              onClick={() => handleDelete(m)}
                              className="p-1.5 rounded-md text-danger-500 hover:bg-danger-50 hover:text-danger-700 disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200">
                <p className="text-sm text-secondary-600">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex space-x-1">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="p-1.5 rounded-md border border-secondary-200 text-secondary-600 hover:bg-secondary-100 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="p-1.5 rounded-md border border-secondary-200 text-secondary-600 hover:bg-secondary-100 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form modal */}
      <MeasurementFormModal
        isOpen={showModal}
        editingMeasurement={editingMeasurement}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default CustomerMeasurements
